import React from 'react'

import LoadingSpinner from '../../../../atoms/LoadingSpinner'

import { initialState, infiniteReducer, Actions } from './reducer'

import useStyles from './styles'

const DEFAULT_SIZE = 20

const calcTopMax = e => {
  const { scrollHeight, clientHeight, scrollTopMax } = e.target

  if (scrollTopMax) {
    return scrollTopMax
  }

  return scrollHeight - clientHeight - 10
}

const Infinite = ({
  id,
  containerRef,
  getData,
  onDataFetched = undefined,
  PAGE_SIZE,
  loadAtTop = true,
  TRIGGER_HEIGHT = 500,
  size = undefined,
  setSize = undefined,
  isLoadingMore = undefined,
  after = undefined,
  isSWR = undefined,
  children = undefined
}) => {
  const [state, dispatch] = React.useReducer(
    infiniteReducer,
    initialState(PAGE_SIZE ?? DEFAULT_SIZE, children.length, loadAtTop)
  )

  const classes = useStyles({ loadAtTop })

  React.useEffect(() => {
    dispatch({
      type: Actions.UPDATE_BOUND,
      childrenLength: children.length,
      top: loadAtTop
    })
  }, [children.length, loadAtTop])

  React.useEffect(() => {
    if (!isLoadingMore && after && !state.canLoad) {
      dispatch({ type: Actions.CAN_LOAD })
    }
  }, [isLoadingMore, after, state])

  React.useEffect(() => {
    if (isSWR && !after) {
      dispatch({ type: Actions.ALL_INFINITE_SWR_LOADED })
    }
  }, [isSWR, after])

  const onScroll = React.useCallback(
    e => {
      if (state.done) {
        return
      }

      const { scrollTop: height } = e.target
      const max = calcTopMax(e)
      const offset = max - height

      let passLimit = height > TRIGGER_HEIGHT
      if (!loadAtTop) {
        passLimit = height < max - TRIGGER_HEIGHT
      }

      if (passLimit || !state.canLoad) {
        return
      }

      dispatch({
        type: Actions.START_LOAD,
        cb: async () => {
          if (!getData) {
            if (!after) {
              dispatch({ type: Actions.ALL_INFINITE_SWR_LOADED })
            }
            if (isLoadingMore || !after) {
              return
            }
            setSize(size + 1)
          } else {
            const { data, extra } = await getData(id)

            if (loadAtTop) {
              // In some cases, the user might keep scrolling to the top making the height 0 and that stops the infinite loader.
              // this is a work around to wait  and force scroll
              setTimeout(() => {
                e.target.scrollTop = calcTopMax(e) - offset
              }, 1)
            }

            onDataFetched && onDataFetched(data, extra)
            dispatch({ type: Actions.FINISH_LOAD })

            if (!extra?.next) {
              dispatch({ type: Actions.ALL_LOADED })
            }
          }
        }
      })
    },
    [
      state.done,
      state.canLoad,
      TRIGGER_HEIGHT,
      id,
      getData,
      onDataFetched,
      loadAtTop,
      size,
      setSize,
      isLoadingMore,
      after
    ]
  )

  React.useEffect(() => {
    const ref = containerRef?.current
    if (!ref) {
      return
    }

    ref.addEventListener('scroll', onScroll)

    return () => {
      ref.removeEventListener('scroll', onScroll)
    }
  }, [containerRef, onScroll])

  const renderable = React.useMemo(
    () =>
      loadAtTop ? children.slice(state.bound) : children.slice(0, state.bound),
    [children, loadAtTop, state.bound]
  )

  return (
    <>
      {!state.canLoad && (
        <div className={classes.root}>
          <LoadingSpinner className={classes.spinner} />
        </div>
      )}

      {renderable}
    </>
  )
}

export default Infinite
